import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPlus } from 'react-icons/bs';

import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';

import { useDeleteToneOfVoice } from '../../../models/toneOfVoices/mutations';
import { useAllToneOfVoices } from '../../../models/toneOfVoices/queries';

import ToneCard from './ToneCard';
import ToneFormModal from './ToneFormModal';

/**
 * ToneOfVoiceSelector - Container component for selecting a tone of voice
 * Displays system tones and user's custom tones with option to create new ones
 */
const ToneOfVoiceSelector = ({ selectedToneId, onSelect, disabled = false }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingTone, setEditingTone] = useState(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [toneToDelete, setToneToDelete] = useState(null);

	const { data: tones = [], isLoading } = useAllToneOfVoices();
	const deleteMutation = useDeleteToneOfVoice();

	const systemTones = tones.filter((t) => t.isSystem);
	const customTones = tones.filter((t) => !t.isSystem);

	const handleEdit = (tone) => {
		setEditingTone(tone);
		setIsModalOpen(true);
	};

	const handleDeleteClick = (toneId) => {
		const tone = tones.find((t) => t.id === toneId);
		setToneToDelete(tone);
		setDeleteConfirmOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!toneToDelete) return;
		try {
			await deleteMutation.mutateAsync(toneToDelete.id);
			// If the deleted tone was selected, clear selection
			if (selectedToneId === toneToDelete.id) {
				onSelect(null);
			}
		} catch {
			// ignored
		} finally {
			setDeleteConfirmOpen(false);
			setToneToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmOpen(false);
		setToneToDelete(null);
	};

	const handleCreateNew = () => {
		setEditingTone(null);
		setIsModalOpen(true);
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditingTone(null);
	};

	if (isLoading) {
		return (
			<div className="space-y-3">
				<div className="h-4 bg-bgBase-500 rounded w-32 animate-pulse" />
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className="h-32 bg-bgBase-400 rounded-card animate-pulse" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div>
				<label className="block text-sm font-medium text-text mb-1">
					Tone of Voice <span className="text-bgBase-800">(empfohlen)</span>
				</label>
				<p className="text-sm text-bgBase-800">Wählen Sie einen Schreibstil für Ihre E-Mails</p>
			</div>

			{/* All Tones in one grid */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				{/* System Tones */}
				{systemTones.map((tone) => (
					<ToneCard
						key={tone.id}
						tone={tone}
						selected={selectedToneId === tone.id}
						onSelect={onSelect}
						disabled={disabled}
					/>
				))}

				{/* Custom Tones */}
				{customTones.map((tone) => (
					<ToneCard
						key={tone.id}
						tone={tone}
						selected={selectedToneId === tone.id}
						onSelect={onSelect}
						onEdit={handleEdit}
						onDelete={handleDeleteClick}
						disabled={disabled}
					/>
				))}

				{/* Create New Tone Card */}
				<button
					type="button"
					onClick={handleCreateNew}
					disabled={disabled}
					className="
						p-4 border-2 border-dashed border-bgBase-600 rounded-card
						hover:border-primary-400 hover:bg-primary-50
						transition-all cursor-pointer
						flex flex-col items-center justify-center gap-2
						text-bgBase-800 hover:text-primary-600
						disabled:opacity-50 disabled:cursor-not-allowed
						min-h-[120px]
					"
				>
					<BsPlus className="w-6 h-6" />
					<span className="text-sm font-medium">Eigenen Stil erstellen</span>
				</button>
			</div>

			{/* Modal for create/edit */}
			<ToneFormModal isOpen={isModalOpen} onClose={handleModalClose} editTone={editingTone} />

			{/* Confirm Delete Modal */}
			<ConfirmModal
				isOpen={deleteConfirmOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Tone of Voice löschen"
				message={`Möchten Sie "${toneToDelete?.name || 'diesen Tone of Voice'}" wirklich löschen?`}
				confirmText="Löschen"
				cancelText="Abbrechen"
			/>
		</div>
	);
};

ToneOfVoiceSelector.propTypes = {
	selectedToneId: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default ToneOfVoiceSelector;
